/**
 * Client-side mirror of the server's slab maths.
 *
 * The tier editor has to show what a change is worth *while* it is being typed,
 * before any request goes out — so the preview has to agree with the server to the
 * rupee. This is a deliberate duplicate of `backend/src/controllers/influencerController.js`;
 * if the payout rules change there, they change here too.
 */

const isOpenEnded = (s) => s.upto === null || s.upto === undefined || s.upto === '';

/**
 * Ascending order, open-ended band last.
 *
 * `slabCalc` walks the bands in sequence, so an out-of-order list silently pays the
 * overlapping slice twice.
 */
export function normalizeSlabs(slabs = []) {
  const closed = slabs.filter((s) => !isOpenEnded(s)).sort((a, b) => Number(a.upto) - Number(b.upto));
  const open = slabs.filter(isOpenEnded);
  return [...closed, ...open.slice(0, 1)];
}

/**
 * Incremental (marginal) payout — each rate applies only to the slice of revenue
 * inside its own band, like income-tax slabs.
 *
 * Revenue above the highest closed band earns nothing unless an open-ended tier is
 * present; `uncovered` reports that so the UI can say so rather than look wrong.
 */
export function slabCalc(base, slabs = []) {
  const ordered = normalizeSlabs(slabs);
  let lo = 0;
  let total = 0;
  const parts = [];

  for (const s of ordered) {
    const hi = isOpenEnded(s) ? Infinity : Number(s.upto);
    const rate = Number(s.rate) || 0;
    const amt = Math.max(0, Math.min(base, hi) - lo);
    const pay = (amt * rate) / 100;
    parts.push({ from: lo, to: hi === Infinity ? null : hi, rate, amt, pay });
    total += pay;
    lo = hi;
    if (hi === Infinity) break;
  }

  return {
    total,
    parts,
    eff: base ? (100 * total) / base : 0,
    uncovered: Math.max(0, base - lo),
  };
}

/**
 * The same rules the server enforces, run early so a bad tier list is explained in
 * place instead of coming back as a 400. Returns a message, or null when sound.
 */
export function validateSlabs(slabs) {
  if (!Array.isArray(slabs) || slabs.length === 0) return 'Add at least one tier';

  let prev = 0;
  for (let i = 0; i < slabs.length; i++) {
    const s = slabs[i];
    const rate = Number(s.rate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      return `Tier ${i + 1}: rate must be between 0 and 100`;
    }

    if (isOpenEnded(s)) {
      if (i !== slabs.length - 1) return 'Only the last tier may be open-ended';
      continue;
    }

    const upto = Number(s.upto);
    if (!Number.isFinite(upto) || upto <= 0) return `Tier ${i + 1}: “up to” must be a positive number`;
    if (upto <= prev) return `Tier ${i + 1}: “up to” must be greater than ${prev.toLocaleString('en-IN')}`;
    prev = upto;
  }
  return null;
}

/** The tier ladder a brand-new influencer starts on. */
export const DEFAULT_SLABS = () => [
  { upto: 100000, rate: 10 },
  { upto: 300000, rate: 15 },
  { upto: null, rate: 20 },
];
